import React, { useContext, useState, useEffect } from 'react';
import { Navbar, Container, Nav, Dropdown, Modal, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authAPI, userSettingsAPI, clientAPI, menuAPI, permissionsAPI } from '../api/api';
import { FiLogOut, FiUser, FiMenu, FiCamera, FiLock, FiSun } from 'react-icons/fi';
import './TopNav.css';

function TopNav({ showMenu = false }) {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuHierarchy, setMenuHierarchy] = useState([]);
  const [tablePermissions, setTablePermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileData, setProfileData] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [clientLogo, setClientLogo] = useState('/aaimsdm/logo-banner.png');
  const [companyName, setCompanyName] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [editProfileData, setEditProfileData] = useState({ email: '', fullName: '' });

  // Apply saved theme on mount and load profile
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
    document.body.className = `theme-${savedTheme}`;
    
    // Load profile on mount for header display
    fetchProfile();
    
    // Load client logo if available
    loadClientLogo();
  }, [user?.clientId]);

  // Fetch menu if showMenu is true
  useEffect(() => {
    if (showMenu && user) {
      const userRole = user.Role || user.role || '';
      const isAdminUser = userRole.toLowerCase() === 'admin';
      setIsAdmin(isAdminUser);
      
      (async () => {
        try {
          const perms = await fetchTablePermissions();
          await fetchMenuHierarchy(perms);
        } catch (error) {
          // Error fetching menu
        }
      })();
    }
  }, [showMenu, user]);

  const fetchTablePermissions = async () => {
    try {
      const sessionRes = await authAPI.checkSession();
      if (sessionRes.data.success && sessionRes.data.user) {
        const userData = sessionRes.data.user;
        const userRole = userData.Role || userData.role || '';
        
        if (userRole.toLowerCase() === 'admin') {
          setIsAdmin(true);
          return {};
        }

        const groupIds = userData.groupIds || [userData.groupId];
        const groupId = groupIds[0];

        if (groupId) {
          const permRes = await permissionsAPI.getGroupPermissions(groupId);
          if (permRes.data.success && permRes.data.tablePermissions) {
            const perms = {};
            permRes.data.tablePermissions.forEach(tp => {
              perms[tp.TableName] = {
                CanView: tp.CanView === 1 || tp.CanView === true
              };
            });
            setTablePermissions(perms);
            return perms;
          }
        }
      }
    } catch (error) {
      // Error fetching permissions
    }
    return {};
  };

  const fetchMenuHierarchy = async (perms = {}) => {
    try {
      const response = await menuAPI.getMenuHierarchy();
      if (response.data && response.data.success) {
        let hierarchy = response.data.hierarchy || [];
        
        const userRole = user?.Role || user?.role || '';
        const isAdminUser = userRole.toLowerCase() === 'admin';
        
        if (user && user.clientId && !isAdminUser) {
          hierarchy = await filterMenuByPermissions(hierarchy, perms);
        }
        
        // Show Configuration folder only to Administrator group
        const showConfig = isAdminUser || (user?.groupNames || user?.roles || []).some(g => /administrator/i.test(g));
        hierarchy = hierarchy.filter(item => !item.isPagesFolder || showConfig);
        
        setMenuHierarchy(hierarchy);
      } else {
        setMenuHierarchy([]);
      }
    } catch (error) {
      setMenuHierarchy([]);
    }
  };

  const filterMenuByPermissions = async (items, perms) => {
    const filtered = [];
    
    for (const item of items) {
      if (item.Name === 'Manage Menu Items' || item.Name === 'Menus' || item.Name === 'MGB Setup') {
        continue;
      }
      // Configuration folder - admin only
      if (item.isPagesFolder) {
        continue;
      }

      // MGB_Menus items (pages) should always be shown if they have a link
      if (item.isMGBMenu && item.Link) {
        filtered.push(item);
        continue;
      }

      if (item.Folder) {
        if (item.children && item.children.length > 0) {
          item.children = await filterMenuByPermissions(item.children, perms);
          if (item.children.length > 0) {
            filtered.push(item);
          }
        }
      } else if (item.LinkRecordID && item.LinkRecordID !== -1) {
        try {
          const response = await menuAPI.getMenuItemTableName(item.MenuItemID);
          if (response.status === 200 && response.data.success) {
            const tableName = response.data.tableName;
            item.tableName = tableName;
            
            if (perms[tableName] && perms[tableName].CanView) {
              filtered.push(item);
            }
          }
        } catch (error) {
          // Skip items that can't be resolved
        }
      } else {
        filtered.push(item);
      }
    }
    
    return filtered;
  };

  const handleMenuClick = async (item) => {
    try {
      // If item is from MGB_Menus (has isMGBMenu flag), handle external link
      if (item.isMGBMenu && item.Link) {
        // Check if it's an external URL (starts with http:// or https://)
        if (item.Link.startsWith('http://') || item.Link.startsWith('https://')) {
          window.open(item.Link, '_blank');
        } else {
          // Internal link
          navigate(item.Link);
        }
        return;
      }

      if (item.Link) {
        navigate(item.Link);
        return;
      }

      if (item.LinkRecordID && item.LinkRecordID !== -1) {
        const response = await menuAPI.getMenuItemTableName(item.MenuItemID);
        
        if (response.data.success) {
          const tableName = response.data.tableName;
          
          if (!isAdmin && user && user.clientId) {
            if (!tablePermissions[tableName] || !tablePermissions[tableName].CanView) {
              alert('You do not have permission to view this table');
              return;
            }
          }
          
          navigate(`/tables/${tableName}`);
        }
      }
    } catch (error) {
      // Error handling menu item click
    }
  };

  const renderTopMenuItems = (items) => {
    if (!items || items.length === 0) return null;

    return items.map(item => {
      const isFolder = item.Folder === 1 || item.Folder === true;
      const hasChildren = isFolder && item.children && item.children.length > 0;

      if (hasChildren) {
        return (
          <Dropdown key={item.MenuItemID} className="me-2">
            <Dropdown.Toggle 
              variant="link" 
              className="text-decoration-none top-nav-menu-item"
              style={{ 
                padding: '8px 12px',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                color: '#333333',
                border: 'none',
                backgroundColor: 'transparent'
              }}
            >
              {item.Name}
            </Dropdown.Toggle>
            <Dropdown.Menu 
              style={{
                marginTop: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            >
              {item.children.map(child => {
                const childHasChildren = child.Folder && child.children && child.children.length > 0;
                
                if (childHasChildren) {
                  return (
                    <Dropdown key={child.MenuItemID} drop="end" className="d-inline-block w-100">
                      <Dropdown.Toggle 
                        as={Dropdown.Item}
                        variant="link"
                        className="text-decoration-none d-flex justify-content-between align-items-center"
                        style={{ padding: '8px 16px' }}
                      >
                        <span>{child.Name}</span>
                        <span style={{ marginLeft: '8px' }}>›</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {child.children.map(grandchild => (
                          <Dropdown.Item 
                            key={grandchild.MenuItemID} 
                            onClick={() => handleMenuClick(grandchild)}
                            style={{ padding: '8px 16px' }}
                          >
                            {grandchild.Name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  );
                }
                
                return (
                  <Dropdown.Item 
                    key={child.MenuItemID} 
                    onClick={() => handleMenuClick(child)}
                    style={{ 
                      padding: '8px 16px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {child.Name}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        );
      }

      return (
        <Nav.Link 
          key={item.MenuItemID} 
          onClick={() => handleMenuClick(item)}
          className="me-2 top-nav-menu-item"
          style={{ 
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            color: '#333333'
          }}
        >
          {item.Name}
        </Nav.Link>
      );
    });
  };

  const loadClientLogo = async () => {
    try {
      const storedLogo = sessionStorage.getItem('clientLogo');
      const storedCompanyName = sessionStorage.getItem('companyName');
      
      if (storedLogo) {
        setClientLogo(storedLogo);
      }
      
      if (storedCompanyName) {
        setCompanyName(storedCompanyName);
        return;
      }
      
      if (user?.clientId) {
        const res = await clientAPI.getClientInfo(user.clientId);
        if (res.data.data?.CompanyLogoPath) {
          setClientLogo(res.data.data.CompanyLogoPath);
          sessionStorage.setItem('clientLogo', res.data.data.CompanyLogoPath);
        }
        if (res.data.data?.CompanyName) {
          setCompanyName(res.data.data.CompanyName);
          sessionStorage.setItem('companyName', res.data.data.CompanyName);
        }
      }
    } catch (err) {
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      sessionStorage.removeItem('clientLogo');
      sessionStorage.removeItem('companyName');
      navigate('/login');
    } catch (error) {
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    document.body.className = `theme-${newTheme}`;
  };

  const fetchProfile = async () => {
    try {
      const response = await userSettingsAPI.getProfile();
      if (response.data.success) {
        setProfileData(response.data.profile);
        setEditProfileData({
          email: response.data.profile.Email || '',
          fullName: response.data.profile.FullName || ''
        });
      }
    } catch (err) {
      setError('Failed to load profile');
    }
  };

  const handleProfileUpdate = async () => {
    setMessage('');
    setError('');

    if (!editProfileData.email) {
      setError('Email is required');
      return;
    }

    try {
      const response = await userSettingsAPI.updateProfile({
        email: editProfileData.email,
        fullName: editProfileData.fullName
      });
      if (response.data.success) {
        setMessage('Profile updated successfully');
        setEditingProfile(false);
        await fetchProfile();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handlePasswordSubmit = async () => {
    setMessage('');
    setError('');

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await userSettingsAPI.updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      if (response.data.success) {
        setMessage('Password updated successfully');
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setMessage('');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMessage('');
    setError('');

    // In a real application, you would upload the file to a server
    // For now, we'll use a data URL
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const dataUrl = event.target?.result;
        const response = await userSettingsAPI.updateProfilePhoto(dataUrl);
        if (response.data.success) {
          setMessage('Profile photo updated successfully');
          setTimeout(() => {
            setShowPhotoModal(false);
            setMessage('');
          }, 1500);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to upload photo');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Navbar bg="dark" variant="dark" sticky="top" className="top-nav" expand="lg">
      <Container fluid>
        <Navbar.Brand as="a" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', marginRight: '30px' }} className="brand">
          <img src={clientLogo} alt="AI-Enhanced Analytics & Integration for Modern Solutions" className="brand-logo me-2" onError={() => setClientLogo('/aaimsdm/logo-banner.png')} />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          {showMenu && (
            <Nav className="me-auto d-flex align-items-center flex-wrap">
              {menuHierarchy.length > 0 ? (
                renderTopMenuItems(menuHierarchy)
              ) : (
                <Nav.Link style={{ cursor: 'default', color: '#333333' }}>
                  Loading menu...
                </Nav.Link>
              )}
            </Nav>
          )}
          {!showMenu && (
            <div style={{ marginLeft: 'auto', marginRight: 'auto', color: 'white', fontSize: '14px' }}>
              Welcome, <strong>{user?.fullName || user?.username}</strong>
            </div>
          )}
          <Nav className="ms-auto">
          <Dropdown align="end">
            <Dropdown.Toggle 
              variant="dark" 
              className="nav-dropdown d-flex align-items-center"
              id="user-dropdown"
            >
              <div className="nav-profile-container">
                <FiUser size={28} className="me-2" />
              </div>
              <span>{user?.fullName || user?.username}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item disabled className="dropdown-header">
                <strong>{user?.fullName || user?.username}</strong>
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => setShowProfileModal(true)}>
                <FiUser className="me-2" />
                View Profile
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowPhotoModal(true)}>
                <FiCamera className="me-2" />
                Set Profile Photo
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowPasswordModal(true)}>
                <FiLock className="me-2" />
                Update Password
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowThemeModal(true)}>
                <FiSun className="me-2" />
                Theme Setup
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout}>
                <FiLogOut className="me-2" />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>

      {/* Profile Modal */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} onShow={fetchProfile} centered>
        <Modal.Header closeButton>
          <Modal.Title>My Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {message && <Alert variant="success">{message}</Alert>}
          <div className="profile-section">
            <div className="text-center mb-3">
              {profileData?.ProfilePhotoURL ? (
                <img 
                  src={profileData.ProfilePhotoURL} 
                  alt="Profile" 
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                />
              ) : (
                <div 
                  style={{
                    fontSize: '64px',
                    margin: '20px 0'
                  }}
                >
                  👤
                </div>
              )}
            </div>
            {profileData && !editingProfile ? (
              <>
                <p><strong>Username:</strong> {profileData.UserName}</p>
                <p><strong>Full Name:</strong> {profileData.FullName || 'N/A'}</p>
                <p><strong>Email:</strong> {profileData.Email || 'N/A'}</p>
                <p><strong>Role:</strong> {profileData.Role || 'User'}</p>
                <p><strong>Company:</strong> {companyName || 'N/A'}</p>
              </>
            ) : editingProfile ? (
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editProfileData.fullName}
                    onChange={(e) => setEditProfileData({...editProfileData, fullName: e.target.value})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editProfileData.email}
                    onChange={(e) => setEditProfileData({...editProfileData, email: e.target.value})}
                  />
                </Form.Group>
              </Form>
            ) : (
              <p className="text-muted">Loading profile...</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {!editingProfile ? (
            <>
              <Button variant="secondary" onClick={() => {
                setShowProfileModal(false);
                setMessage('');
                setError('');
                setProfileData(null);
              }}>
                Close
              </Button>
              <Button variant="primary" onClick={() => setEditingProfile(true)}>
                Edit Profile
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => {
                setEditingProfile(false);
                setMessage('');
                setError('');
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleProfileUpdate}>
                Save Changes
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Password Modal */}
      <Modal show={showPasswordModal} onHide={() => {
        setShowPasswordModal(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setMessage('');
        setError('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <Form.Control 
                type="password" 
                placeholder="Enter current password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control 
                type="password" 
                placeholder="Enter new password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control 
                type="password" 
                placeholder="Confirm new password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPasswordModal(false);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setMessage('');
            setError('');
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePasswordSubmit}>
            Update Password
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Profile Photo Modal */}
      <Modal show={showPhotoModal} onHide={() => {
        setShowPhotoModal(false);
        setMessage('');
        setError('');
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Set Profile Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="text-center">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handlePhotoUpload}
              className="form-control"
            />
            <p className="text-muted mt-3">Supported formats: JPG, PNG, GIF (Max 5MB)</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPhotoModal(false);
            setMessage('');
            setError('');
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Theme Modal */}
      <Modal show={showThemeModal} onHide={() => setShowThemeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Theme Setup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="theme-options">
            <h6>Select Theme:</h6>
            <div className="theme-buttons">
              <Button 
                variant={theme === 'light' ? 'primary' : 'outline-primary'}
                className="me-2"
                onClick={() => handleThemeChange('light')}
              >
                ☀️ Light Theme
              </Button>
              <Button 
                variant={theme === 'dark' ? 'primary' : 'outline-primary'}
                className="me-2"
                onClick={() => handleThemeChange('dark')}
              >
                🌙 Dark Theme
              </Button>
              <Button 
                variant={theme === 'auto' ? 'primary' : 'outline-primary'}
                onClick={() => handleThemeChange('auto')}
              >
                🔄 Auto (System)
              </Button>
            </div>
            <p className="text-muted mt-3 mb-0">Current Theme: <strong>{theme}</strong></p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowThemeModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Navbar>
  );
}

export default TopNav;
